import React from "react";
import { Link, useLocation } from "react-router-dom";
import { LogOut, User, ShoppingBag, Settings, Music, Heart, Lock } from "lucide-react";
import JazzHeader from "../../homepage_components/JazzHeader";
import JazzFooter from "../../homepage_components/JazzFooter";
import { useAuth } from "../../services/AuthService";

export default function UserDashboardLayout({ children, title, subtitle }) {
    const { user, logout } = useAuth();
    const location = useLocation();

    const handleLogout = () => {
        logout();
    };

    const navItems = [
        {
            label: "Overview",
            path: "/dashboard",
            icon: <User size={18} />,
            isActive: location.pathname === "/dashboard"
        },
        {
            label: "Purchase History",
            path: "/dashboard/purchase-history",
            icon: <ShoppingBag size={18} />,
            isActive: location.pathname === "/dashboard/purchase-history"
        },
        {
            label: "Change Password",
            path: "/dashboard/change-password",
            icon: <Lock size={18} />,
            isActive: location.pathname === "/dashboard/change-password"
        },
        {
            label: "My Collection",
            path: "#collection",
            icon: <Music size={18} />,
            isActive: false,
            comingSoon: true
        },
        {
            label: "Wishlist",
            path: "#wishlist",
            icon: <Heart size={18} />,
            isActive: false,
            comingSoon: true
        },
        {
            label: "Account Settings",
            path: "#settings",
            icon: <Settings size={18} />,
            isActive: false,
            comingSoon: true
        },
    ];

    return (
        <div className="min-h-screen bg-[#fafafa] flex flex-col font-sans">
            <JazzHeader />

            <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-24 sm:px-6 lg:px-8">
                <div className="mb-12 animate-fade-in">
                    <h1 className="text-3xl md:text-4xl font-serif italic text-black mb-3">
                        {title || `Welcome back, ${user?.full_name || "Music Lover"}`}
                    </h1>
                    <p className="text-[10px] font-bold tracking-[2px] uppercase text-black/80">
                        {subtitle || "Manage your account and view your digital collection."}
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    {/* Sidebar */}
                    <aside className="lg:col-span-1 space-y-2">
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-black/5 mb-6">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 bg-jazz-brown/10 rounded-full flex items-center justify-center text-jazz-brown">
                                    <User size={24} />
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold text-black">{user?.full_name || "User"}</h3>
                                    <p className="text-[10px] tracking-[1px] uppercase text-black/80">{user?.email || "user@example.com"}</p>
                                </div>
                            </div>
                            <button
                                onClick={handleLogout}
                                className="w-full py-3 px-4 bg-red-50 text-red-600 hover:bg-red-100 font-bold text-[10px] tracking-[2px] uppercase rounded-xl transition-colors flex items-center justify-center gap-2"
                            >
                                <LogOut size={14} />
                                Sign Out
                            </button>
                        </div>

                        <nav className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden">
                            <ul className="divide-y divide-black/5">
                                {navItems.map((item) => (
                                    <li key={item.label}>
                                        {item.path.startsWith("#") ? (
                                            <div
                                                className={`group relative block px-6 py-4 flex items-center gap-3 text-sm font-semibold transition-colors border-l-[6px] ${item.isActive
                                                    ? "text-black border-jazz-brown bg-jazz-brown/5 font-bold"
                                                    : "text-black/30 cursor-not-allowed border-transparent"
                                                    }`}
                                            >
                                                {React.cloneElement(item.icon, {
                                                    className: item.isActive ? "text-jazz-brown" : "opacity-50"
                                                })}
                                                <span>{item.label}</span>
                                                {item.comingSoon && (
                                                    <span className="ml-auto text-[8px] tracking-[1px] uppercase bg-black/5 text-black/60 px-2 py-1 rounded-md font-bold group-hover:bg-jazz-brown/10 group-hover:text-jazz-brown transition-colors">
                                                        Coming Soon
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <Link
                                                to={item.path}
                                                className={`block px-6 py-4 flex items-center gap-3 text-sm font-semibold transition-colors border-l-[6px] ${item.isActive
                                                    ? "text-black border-jazz-brown bg-jazz-brown/5 font-bold"
                                                    : "text-black/70 hover:text-black hover:bg-black/5 border-transparent"
                                                    }`}
                                            >
                                                {React.cloneElement(item.icon, {
                                                    className: item.isActive ? "text-jazz-brown" : ""
                                                })}
                                                {item.label}
                                            </Link>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </aside>

                    {/* Main Content Area */}
                    <div className="lg:col-span-3 space-y-8 animate-fade-in" style={{ animationDelay: '100ms' }}>
                        {children}
                    </div>
                </div>
            </main>

            <JazzFooter />

            <style jsx="true" global="true">{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out both;
        }
      `}</style>
        </div>
    );
}
